import { fetchWidgetData } from '../api/client';
import { buildSetDataPayloadFromWidgetResponse } from './widgetDataPayload';
import { applyTheme, detectColorScheme } from '../utils/theme';
import { sendOccurrenceEventNavigate } from '../utils/postMessage';
import type { EventData } from '../types';
import type { EventOccurrenceEntry } from './eventOccurrenceDates';
import { eventDocumentMatchesTargetKey, sameUtcCalendarDay } from './eventOccurrenceDates';
import type { WidgetAction } from '../store';

export type ApplyOccurrenceOptions = {
  /** First `SET_DATA` event id (bootstrap) — used to refetch parent when returning from a linked child. */
  widgetBootstrapEventId: string | null;
  /** First occurrence ISO from bootstrap (see store) — when user picks this day while on a child, reload parent. */
  widgetBootstrapFirstOccurrenceIso: string | null;
};

/**
 * When the user picks an occurrence: optionally reload widget data for a linked `targetEventId`,
 * update selected occurrence, clear cart/hold, and notify the embed parent (meeteor:occurrence_event).
 * Same event + different date: no network fetch, only store updates — unless a prior navigation loaded a
 * child event; selecting the bootstrap “first” day again refetches the parent.
 */
export async function applyOccurrenceDateSelection(
  dispatch: (action: WidgetAction) => void,
  widgetId: string,
  currentEvent: EventData | null,
  entry: EventOccurrenceEntry,
  options?: ApplyOccurrenceOptions,
): Promise<void> {
  const { widgetBootstrapEventId, widgetBootstrapFirstOccurrenceIso } = options ?? {
    widgetBootstrapEventId: null,
    widgetBootstrapFirstOccurrenceIso: null,
  };

  const targetKey = entry.targetEventId?.trim();
  const mustFetchTarget =
    Boolean(targetKey) &&
    Boolean(currentEvent) &&
    !eventDocumentMatchesTargetKey(currentEvent, targetKey!);

  const onChild = Boolean(
    currentEvent?._id && widgetBootstrapEventId && currentEvent._id !== widgetBootstrapEventId,
  );
  const restoreParent =
    onChild &&
    !targetKey &&
    Boolean(widgetBootstrapEventId) &&
    Boolean(widgetBootstrapFirstOccurrenceIso) &&
    sameUtcCalendarDay(entry.iso, widgetBootstrapFirstOccurrenceIso!);

  const runSetDataFromFetch = (res: Awaited<ReturnType<typeof fetchWidgetData>>) => {
    const payload = buildSetDataPayloadFromWidgetResponse(res);
    const scheme = detectColorScheme();
    applyTheme(
      payload.widget!.theme,
      scheme === 'dark' ? true : scheme === 'light' ? false : undefined,
    );
    dispatch({ type: 'SET_DATA', payload });
  };

  if (mustFetchTarget && targetKey) {
    const res = await fetchWidgetData(widgetId, { eventId: targetKey });
    runSetDataFromFetch(res);
  } else if (restoreParent) {
    const res = await fetchWidgetData(widgetId, { eventId: widgetBootstrapEventId! });
    runSetDataFromFetch(res);
  } else {
    dispatch({ type: 'CLEAR_SELECTION' });
    dispatch({ type: 'SET_SELECTED_OCCURRENCE_ISO', iso: entry.iso });
  }

  dispatch({ type: 'SET_SELECTED_OCCURRENCE_ISO', iso: entry.iso });

  let notifyId: string | null = null;
  if (mustFetchTarget && targetKey) {
    notifyId = targetKey;
  } else if (restoreParent) {
    notifyId = widgetBootstrapEventId!;
  } else if (currentEvent?._id) {
    notifyId = currentEvent._id;
  }
  if (notifyId) {
    sendOccurrenceEventNavigate(notifyId, entry.iso);
  }
}
